import {
  PACKAGE_STATUSES,
  type PackageStatus,
  DEMAND_SOURCES,
  type DemandSource,
  READINESS,
  type Readiness,
  STOCK_DOC_KINDS,
  type StockDocKind,
  type StockDocStatus,
  PURCHASE_PAYMENT_STATUSES,
  PURCHASE_STATUSES,
  type PurchasePaymentStatus,
  type PurchaseStatus,
  CASE_FLAGS,
  CONTRACT_STATUSES,
  type ContractStatus,
  EXECUTION_STATES,
  type ExecutionState,
  EXT_STATUS_LABELS,
  PAYMENT_ITEM_STATUSES,
  type PaymentItemStatus,
  TASK_STATUSES,
  type TaskStatus,
  VERSION_STATUSES,
  type VersionStatus,
  CASE_STATUSES,
  type CaseFlag,
  type CaseStatus,
  type CompanyDto,
  readableTextColor,
  SALES_STAGES,
  type SalesStage,
  type TradeDto,
  type VatCheckDto,
  type VatStatus,
  datePl,
  FLEET_COST_KINDS,
  type FleetCostKind,
  OBLIGATION_STATES,
  OBLIGATION_STATUSES,
  type ObligationState,
  type ObligationStatus,
  ODOMETER_STATES,
  type OdometerState,
  REPORT_STATUSES,
  type ReportStatus,
  UNAVAILABILITY_REASONS,
  type UnavailabilityReason,
  VEHICLE_STATUSES,
  type VehicleStatus,
  VISIT_STATUSES,
  type VisitStatus,
} from '@bqcrm/shared';
import { Badge, Tooltip } from '@mantine/core';
import { useCompanies, useTrades } from '../data';
import { CompanyBadge } from './CompanyBadge';
import { formatDateTime } from '../format';

const SALES_STAGE_COLORS: Record<SalesStage, string> = {
  otwarty: 'blue',
  ofertowanie: 'cyan',
  negocjacje: 'violet',
  zaakceptowany: 'teal',
  umowa_podpisana: 'green',
  wstrzymany: 'yellow',
  przegrany: 'red',
};

export const SalesStageBadge = ({ stage }: { stage: SalesStage }) => (
  <Badge color={SALES_STAGE_COLORS[stage]} variant="light" size="sm">
    {SALES_STAGES[stage]}
  </Badge>
);

/** Znacznik branży: kolor i skrót, pełna nazwa w podpowiedzi. */
export function TradeBadge({ code, trade, full }: { code: string; trade?: TradeDto; full?: boolean }) {
  const { data } = useTrades();
  const t = trade ?? data?.find((x) => x.code === code);
  const bg = t?.color ?? '#868e96';
  return (
    <Tooltip label={t?.name ?? code} disabled={full}>
      {/* kolor tekstu wg jasności tła — biały na jasnym kolorze branży był nieczytelny */}
      <Badge radius="sm" style={{ backgroundColor: bg }} c={readableTextColor(bg)} size="sm">
        {full && t ? `${code} · ${t.name}` : code}
      </Badge>
    </Tooltip>
  );
}

export function CompanyBadgeById({ id, full }: { id: string; full?: boolean }) {
  const { data } = useCompanies();
  const c: CompanyDto | undefined = data?.find((x) => x.id === id);
  return c ? <CompanyBadge company={c} full={full} /> : null;
}

const CASE_STATUS_COLORS: Record<CaseStatus, string> = {
  nowa: 'orange',
  weryfikacja: 'yellow',
  oferta: 'blue',
  umowa: 'violet',
  realizacja: 'teal',
  weryfikacja_koncowa: 'cyan',
  zamknieta: 'eko',
  odrzucona: 'dark',
  przegrana: 'red',
  anulowana: 'gray',
};

/** Status główny sprawy (docs/10 §3.8) — jedna faza procesu. */
export const CaseStatusBadge = ({ status, size }: { status: CaseStatus; size?: 'sm' | 'md' }) => (
  <Badge color={CASE_STATUS_COLORS[status] ?? 'gray'} variant="light" size={size} style={{ maxWidth: 'none', flexShrink: 0, overflow: 'visible' }} styles={{ label: { overflow: 'visible' } }}>
    {CASE_STATUSES[status] ?? status}
  </Badge>
);

const FLAG_COLORS = { todo: 'orange', wait: 'gray', late: 'red', ok: 'eko' } as const;

/** Flagi sprawy — co jeszcze trwa obok statusu (oczekuje na podpis, płatność po terminie…). */
export const CaseFlagBadges = ({ flags, size = 'xs' }: { flags: CaseFlag[]; size?: 'xs' | 'sm' }) => (
  <>
    {flags.map((f) => (
      <Badge key={f} size={size} variant="outline" color={FLAG_COLORS[CASE_FLAGS[f]?.tone ?? 'wait']}>
        {CASE_FLAGS[f]?.label ?? f}
      </Badge>
    ))}
  </>
);

type BadgeSize = 'xs' | 'sm' | 'md';
/** „nieopłacona” → „Nieopłacona” — etykiety słowników pisane małą literą pokazujemy zdaniem. */
const sentence = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

const VERSION_STATUS_COLORS: Record<VersionStatus, string> = {
  roboczy: 'gray',
  wydany: 'blue',
  zaakceptowany: 'green',
  zastapiony: 'dark',
  wygasly: 'orange',
  wycofany: 'red',
  odrzucony: 'orange',
  do_zmiany: 'yellow',
};

/** Status wersji oferty. */
/**
 * Status wersji oferty. `caseClosed` — sprawa zamknięta: oferta czekająca na decyzję (wydana, do zmiany) jest nieaktualna
 * („Nieaktualna · Sprawa zamknięta”, jak w strefie klienta). Dane się nie zmieniają — wznowienie sprawy przywraca status (QA qa10311123 3.12).
 */
export const VersionStatusBadge = ({ status, size = 'sm', caseClosed = false }: { status: VersionStatus; size?: BadgeSize; caseClosed?: boolean }) =>
  caseClosed && (status === 'wydany' || status === 'do_zmiany') ? (
    <Badge color="gray" variant="light" size={size}>
      Nieaktualna · Sprawa zamknięta
    </Badge>
  ) : (
    <Badge color={VERSION_STATUS_COLORS[status]} variant="light" size={size}>
      {VERSION_STATUSES[status]}
    </Badge>
  );

const CONTRACT_STATUS_COLORS: Record<ContractStatus, string> = { roboczy: 'gray', wydany: 'blue', podpisany: 'green', anulowany: 'red' };

/** Status umowy / aneksu. */
export const ContractStatusBadge = ({ status, size = 'sm' }: { status: ContractStatus; size?: BadgeSize }) => (
  <Badge color={CONTRACT_STATUS_COLORS[status]} variant="light" size={size}>
    {CONTRACT_STATUSES[status]}
  </Badge>
);

const PAYMENT_STATUS_COLORS: Record<PaymentItemStatus, string> = { oczekuje: 'gray', czesciowo: 'yellow', oplacona: 'green', anulowana: 'dark' };

/** Status pozycji harmonogramu płatności; nieopłacona po terminie — czerwona „Po terminie”. */
export function PaymentStatusBadge({ status, overdue, size = 'sm' }: { status: PaymentItemStatus; overdue?: boolean; size?: BadgeSize }) {
  const late = !!overdue && (status === 'oczekuje' || status === 'czesciowo');
  return (
    <Badge size={size} variant="light" color={late ? 'red' : PAYMENT_STATUS_COLORS[status]}>
      {late ? (status === 'czesciowo' ? 'Częściowo, po terminie' : 'Po terminie') : PAYMENT_ITEM_STATUSES[status]}
    </Badge>
  );
}

/** Stan zapłaty faktury w Fakturowni (issued / sent / partial / paid / rejected); brak stanu = nieopłacona. */
export function InvoicePayStatusBadge({ extStatus, overdue, size = 'sm' }: { extStatus: string | null | undefined; overdue?: boolean; size?: BadgeSize }) {
  const color = extStatus === 'paid' ? 'green' : extStatus === 'rejected' || overdue ? 'red' : extStatus === 'partial' ? 'yellow' : 'gray';
  return (
    <Badge size={size} variant="light" color={color}>
      {sentence((extStatus && EXT_STATUS_LABELS[extStatus]) || extStatus || 'nieopłacona')}
    </Badge>
  );
}

/** Dokument z konta testowego Fakturowni. */
export const TestModeBadge = ({ size = 'xs', ml }: { size?: BadgeSize; ml?: number }) => (
  <Badge size={size} color="yellow" variant="light" ml={ml}>
    Test
  </Badge>
);

const TASK_STATUS_COLORS: Record<TaskStatus, string> = { do_zaplanowania: 'gray', zaplanowane: 'blue', w_toku: 'orange', wstrzymane: 'red', zakonczone: 'green', anulowane: 'gray' };

/** Status zadania (prace w planowaniu / w sprawie). */
export const TaskStatusBadge = ({ status, size = 'sm' }: { status: TaskStatus; size?: BadgeSize }) => (
  <Badge size={size} variant="light" color={TASK_STATUS_COLORS[status] ?? 'gray'}>
    {TASK_STATUSES[status] ?? status}
  </Badge>
);

/** Priorytet — znacznik tylko dla pilnych. */
export const PriorityBadge = ({ priority, size = 'sm' }: { priority: string | null | undefined; size?: BadgeSize }) =>
  priority === 'pilny' ? (
    <Badge size={size} color="red" variant="filled">
      Pilne
    </Badge>
  ) : null;

const EXECUTION_STATE_COLORS: Record<ExecutionState, string> = {
  nie_dotyczy: 'gray',
  oczekuje_na_warunki: 'red',
  do_zaplanowania: 'teal',
  zaplanowany: 'blue',
  zaplanowany_warunkowo: 'blue',
  w_realizacji: 'orange',
  wstrzymany: 'yellow',
  do_odbioru: 'blue',
  odebrany: 'green',
};

/** Stan realizacji zakresu umowy. */
export const ExecutionStateBadge = ({ state, size = 'sm' }: { state: ExecutionState; size?: BadgeSize }) => (
  <Badge size={size} variant="light" color={EXECUTION_STATE_COLORS[state] ?? 'gray'}>
    {EXECUTION_STATES[state] ?? state}
  </Badge>
);

const PURCHASE_STATUS_COLORS: Record<PurchaseStatus, string> = { nowa: 'blue', zaakceptowana: 'teal', zaksiegowana: 'green', odrzucona: 'gray' };

/** Faktura zakupu: praca księgowej (do sprawdzenia → zaakceptowana → zaksięgowana; odrzucona). */
export const PurchaseStatusBadge = ({ status, size = 'sm' }: { status: PurchaseStatus; size?: BadgeSize }) => (
  <Badge size={size} variant={status === 'nowa' ? 'filled' : 'light'} color={PURCHASE_STATUS_COLORS[status] ?? 'gray'}>
    {PURCHASE_STATUSES[status] ?? status}
  </Badge>
);

/** Faktura zakupu: zapłata (po terminie — na czerwono). */
export const PurchasePaymentBadge = ({ status, overdue, size = 'sm' }: { status: PurchasePaymentStatus; overdue?: boolean; size?: BadgeSize }) => (
  <Badge size={size} variant="light" color={status === 'zaplacona' ? 'green' : overdue ? 'red' : 'yellow'}>
    {status === 'do_zaplaty' && overdue ? 'Po terminie' : PURCHASE_PAYMENT_STATUSES[status]}
  </Badge>
);

// ── Flota (docs/19) ──

const OBLIGATION_STATE_COLORS: Record<ObligationState, string> = { po_terminie: 'red', blisko: 'orange', brak_danych: 'yellow', ok: 'green', bez_terminu: 'gray' };

/** Stan czynności floty (termin / przebieg); powód w podpowiedzi. */
export const ObligationStateBadge = ({ state, reason, size = 'sm' }: { state: ObligationState; reason?: string; size?: BadgeSize }) => (
  <Tooltip label={reason ?? ''} disabled={!reason}>
    <Badge size={size} variant={state === 'po_terminie' ? 'filled' : 'light'} color={OBLIGATION_STATE_COLORS[state] ?? 'gray'}>
      {OBLIGATION_STATES[state] ?? state}
    </Badge>
  </Tooltip>
);

const OBLIGATION_STATUS_COLORS: Record<ObligationStatus, string> = { otwarta: 'blue', umowiona: 'teal', wykonana: 'green', anulowana: 'gray' };

/** Status czynności floty (do wykonania / umówiona / wykonana / anulowana). */
export const ObligationStatusBadge = ({ status, size = 'sm' }: { status: ObligationStatus; size?: BadgeSize }) => (
  <Badge size={size} variant="light" color={OBLIGATION_STATUS_COLORS[status] ?? 'gray'}>
    {OBLIGATION_STATUSES[status] ?? status}
  </Badge>
);

const REPORT_STATUS_COLORS: Record<ReportStatus, string> = { nowe: 'blue', w_analizie: 'cyan', do_realizacji: 'violet', serwis_zaplanowany: 'teal', w_realizacji: 'orange', zakonczone: 'green', odrzucone: 'gray' };

/** Status zgłoszenia floty. */
export const FleetReportStatusBadge = ({ status, size = 'sm' }: { status: ReportStatus; size?: BadgeSize }) => (
  <Badge size={size} variant={status === 'nowe' ? 'filled' : 'light'} color={REPORT_STATUS_COLORS[status] ?? 'gray'}>
    {REPORT_STATUSES[status] ?? status}
  </Badge>
);

/** Zgłoszenie „nie da się jeździć” — wyróżnienie pilne. */
export const BlocksUseBadge = ({ size = 'sm' }: { size?: BadgeSize }) => (
  <Badge size={size} color="red" variant="filled">
    Nie da się jeździć
  </Badge>
);

/** Status trwały pojazdu (aktywny / wycofany). */
export const VehicleStatusBadge = ({ status, size = 'sm' }: { status: VehicleStatus; size?: BadgeSize }) => (
  <Badge size={size} variant="light" color={status === 'aktywny' ? 'green' : 'gray'}>
    {VEHICLE_STATUSES[status] ?? status}
  </Badge>
);

/** Dostępność pojazdu dziś (niedostępny — przyczyna i data końca w podpowiedzi). */
export const VehicleAvailabilityBadge = ({ unavailable, size = 'sm' }: { unavailable: { reason: UnavailabilityReason; dateTo: string } | null; size?: BadgeSize }) =>
  unavailable ? (
    <Tooltip label={`${UNAVAILABILITY_REASONS[unavailable.reason] ?? unavailable.reason} — do ${datePl(unavailable.dateTo)}`}>
      <Badge size={size} variant="light" color="red">
        Niedostępny
      </Badge>
    </Tooltip>
  ) : (
    <Badge size={size} variant="light" color="green">
      Dostępny
    </Badge>
  );

const ODOMETER_STATE_COLORS: Record<OdometerState, string> = { ok: 'green', do_weryfikacji: 'orange', odrzucony: 'gray' };

/** Stan odczytu przebiegu. */
export const OdometerStateBadge = ({ state, size = 'sm' }: { state: OdometerState; size?: BadgeSize }) => (
  <Badge size={size} variant="light" color={ODOMETER_STATE_COLORS[state] ?? 'gray'}>
    {ODOMETER_STATES[state] ?? state}
  </Badge>
);

const FLEET_COST_KIND_COLORS: Record<FleetCostKind, string> = { faktura: 'blue', reczny: 'grape', przewidywany: 'gray' };

/** Rodzaj kosztu floty: z faktury / ręczny / przewidywany (przewidywane nie są doliczane do sum). */
export const FleetCostKindBadge = ({ kind, size = 'sm' }: { kind: FleetCostKind; size?: BadgeSize }) => (
  <Badge size={size} variant={kind === 'przewidywany' ? 'outline' : 'light'} color={FLEET_COST_KIND_COLORS[kind] ?? 'gray'}>
    {FLEET_COST_KINDS[kind] ?? kind}
  </Badge>
);

const VISIT_STATUS_COLORS: Record<VisitStatus, string> = { planowana: 'blue', w_trakcie: 'orange', zakonczona: 'green', odwolana: 'gray' };

/** Status wizyty w serwisie. */
export const VisitStatusBadge = ({ status, size = 'sm' }: { status: VisitStatus; size?: BadgeSize }) => (
  <Badge size={size} variant="light" color={VISIT_STATUS_COLORS[status] ?? 'gray'}>
    {VISIT_STATUSES[status] ?? status}
  </Badge>
);

const VAT_BADGE: Record<VatStatus, { label: string; color: string }> = {
  Czynny: { label: 'VAT czynny', color: 'green' },
  Zwolniony: { label: 'Zwolniony z VAT', color: 'yellow' },
  Niezarejestrowany: { label: 'Niezarejestrowany w VAT', color: 'red' },
  brak: { label: 'Brak na białej liście VAT', color: 'red' },
};

/** Status z białej listy podatników VAT; w podpowiedzi data sprawdzenia. */
export const VatStatusBadge = ({ check, size = 'sm' }: { check: VatCheckDto; size?: BadgeSize }) => (
  <Tooltip label={`Biała lista VAT, sprawdzono ${formatDateTime(check.checkedAt)}`}>
    <Badge size={size} variant="light" color={VAT_BADGE[check.status].color}>
      {VAT_BADGE[check.status].label}
    </Badge>
  </Tooltip>
);

const STOCK_DOC_COLORS: Record<StockDocKind, string> = { BO: 'gray', PZ: 'teal', WZ: 'orange', WE: 'yellow', ZW: 'cyan', MM: 'indigo', INW: 'grape', KOR: 'grape' };

/** Rodzaj dokumentu magazynowego (PZ, WZ, …) — skrót z pełną nazwą w podpowiedzi. */
export const StockDocKindBadge = ({ kind, size = 'sm' }: { kind: StockDocKind; size?: BadgeSize }) => (
  <Badge size={size} variant="light" color={STOCK_DOC_COLORS[kind] ?? 'gray'} title={STOCK_DOC_KINDS[kind]?.label}>
    {kind}
  </Badge>
);

/** Status dokumentu magazynowego: roboczy (bez wpływu na stan) / zatwierdzony; odwrócony — szary. */
export const StockDocStatusBadge = ({ status, reversed, size = 'sm' }: { status: StockDocStatus; reversed?: boolean; size?: BadgeSize }) => (
  <Badge size={size} variant={status === 'roboczy' ? 'outline' : 'light'} color={reversed ? 'gray' : status === 'roboczy' ? 'yellow' : 'green'}>
    {reversed ? 'Odwrócony' : status === 'roboczy' ? 'Roboczy' : 'Zatwierdzony'}
  </Badge>
);

const READINESS_COLORS: Record<Readiness, string> = { zabezpieczone: 'green', dostawa: 'blue', zagrozone: 'red', brak: 'orange', do_dopasowania: 'yellow' };

/** Gotowość materiałowa realizacji (rezerwacje, nie fizyczne przygotowanie pakietu). */
export const ReadinessBadge = ({ readiness, size = 'sm' }: { readiness: Readiness | null; size?: BadgeSize }) =>
  readiness ? (
    <Badge size={size} variant={readiness === 'zagrozone' ? 'filled' : 'light'} color={READINESS_COLORS[readiness]}>
      {READINESS[readiness]}
    </Badge>
  ) : null;

/** Mały znacznik gotowości materiałowej na pasku planu (kolor = stan; bez znacznika przy „zabezpieczone” i braku potrzeb). */
export const ReadinessMark = ({ readiness }: { readiness: Readiness | null | undefined }) =>
  readiness && readiness !== 'zabezpieczone' ? (
    <span
      title={`Materiały: ${READINESS[readiness]}`}
      aria-label={`Materiały: ${READINESS[readiness]}`}
      style={{ display: 'inline-block', flexShrink: 0, width: 9, height: 9, borderRadius: 2, background: `var(--mantine-color-${READINESS_COLORS[readiness]}-6)`, border: '1px solid rgba(255,255,255,0.8)' }}
    />
  ) : null;

/** Źródło zapotrzebowania: z umowy / od ekipy / ręczne. */
export const DemandSourceBadge = ({ source, size = 'sm' }: { source: DemandSource; size?: BadgeSize }) => (
  <Badge size={size} variant="light" color={source === 'ekipa' ? 'cyan' : source === 'umowa' ? 'indigo' : 'gray'}>
    {DEMAND_SOURCES[source]}
  </Badge>
);

const PACKAGE_COLORS: Record<PackageStatus, string> = {
  do_przygotowania: 'gray',
  w_kompletacji: 'blue',
  czesciowo_gotowy: 'yellow',
  gotowy: 'green',
  czesciowo_wydany: 'teal',
  wydany: 'dark',
  anulowany: 'gray',
};

/** Status pakietu materiałów dla ekipy. */
export const PackageStatusBadge = ({ status, size = 'sm' }: { status: PackageStatus; size?: BadgeSize }) => (
  <Badge size={size} variant={status === 'gotowy' ? 'filled' : 'light'} color={PACKAGE_COLORS[status]}>
    {PACKAGE_STATUSES[status]}
  </Badge>
);
